/**
 * Supabase Data Provider
 * 
 * Implements the DataProvider interface using Supabase as the backend.
 * This is used in "Online" mode when the user has a Supabase account.
 */

import { supabase } from '@/lib/supabase'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { logger } from '@/lib/logger'
import {
  TABLES,
  type DataProvider,
  type QueryOptions,
  type QueryFilter,
  type ChangePayload,
  type Subscription,
  type FileMetadata,
  type AuthState,
  type OperationResult,
  type StandaloneUser
} from './types'

export class SupabaseProvider implements DataProvider {
  private initialized = false
  private authStateCallbacks: Set<(state: AuthState) => void> = new Set()
  private channels: Map<string, RealtimeChannel> = new Map()

  // ==================== CRUD Operations ====================

  async query<T>(table: string, options?: QueryOptions): Promise<T[]> {
    let query = supabase.from(table).select(options?.select || '*')

    // Apply soft delete filter (exclude deleted records unless explicitly requested)
    if (!options?.includeDeleted) {
      query = query.is('deleted_at', null)
    }

    // Apply filters
    if (options?.filters) {
      for (const filter of options.filters) {
        query = this.applyFilter(query, filter)
      }
    }

    // Apply ordering
    if (options?.orderBy) {
      for (const order of options.orderBy) {
        query = query.order(order.column, { ascending: order.ascending ?? true })
      }
    }

    // Apply pagination
    if (options?.limit) {
      query = query.limit(options.limit)
    }
    if (options?.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 1000) - 1)
    }

    const { data, error } = await query

    if (error) {
      logger.error(`[SupabaseProvider] Query error on ${table}:`, error)
      throw error
    }

    return (data || []) as T[]
  }

  async getById<T>(table: string, id: string): Promise<T | null> {
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned - not an error, just null
        return null
      }
      logger.error(`[SupabaseProvider] GetById error on ${table}:`, error)
      throw error
    }

    return data as T
  }

  async insert<T>(table: string, data: Partial<T>): Promise<OperationResult<T>> {
    const { data: result, error } = await supabase
      .from(table)
      .insert(data as any)
      .select()
      .single()

    if (error) {
      logger.error(`[SupabaseProvider] Insert error on ${table}:`, error)
      return { success: false, error: error.message }
    }

    return { success: true, data: result as T }
  }

  async insertMany<T>(table: string, data: Partial<T>[]): Promise<OperationResult<T[]>> {
    const { data: result, error } = await supabase
      .from(table)
      .insert(data as any[])
      .select()

    if (error) {
      logger.error(`[SupabaseProvider] InsertMany error on ${table}:`, error)
      return { success: false, error: error.message }
    }

    return { success: true, data: result as T[] }
  }

  async update<T>(table: string, id: string, data: Partial<T>): Promise<OperationResult<T>> {
    const { data: result, error } = await supabase
      .from(table)
      .update(data as any)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      logger.error(`[SupabaseProvider] Update error on ${table}:`, error)
      return { success: false, error: error.message }
    }

    return { success: true, data: result as T }
  }

  async updateMany<T>(table: string, filters: QueryFilter[], data: Partial<T>): Promise<OperationResult<number>> {
    let query = supabase.from(table).update(data as any)

    for (const filter of filters) {
      query = this.applyFilter(query, filter)
    }

    const { error, count } = await query

    if (error) {
      logger.error(`[SupabaseProvider] UpdateMany error on ${table}:`, error)
      return { success: false, error: error.message }
    }

    return { success: true, data: count || 0 }
  }

  async upsert<T>(table: string, data: Partial<T>, onConflict?: string): Promise<OperationResult<T>> {
    const query = supabase.from(table).upsert(data as any, {
      onConflict: onConflict
    })

    const { data: result, error } = await query.select().single()

    if (error) {
      logger.error(`[SupabaseProvider] Upsert error on ${table}:`, error)
      return { success: false, error: error.message }
    }

    return { success: true, data: result as T }
  }

  async delete(table: string, id: string, deletionBatchId?: string): Promise<OperationResult> {
    // Soft delete: set deleted_at timestamp and optional batch ID
    const updateData: { deleted_at: string; deletion_batch_id?: string } = { 
      deleted_at: new Date().toISOString() 
    }
    if (deletionBatchId) {
      updateData.deletion_batch_id = deletionBatchId
    }
    
    const { error } = await supabase
      .from(table)
      .update(updateData)
      .eq('id', id)

    if (error) {
      logger.error(`[SupabaseProvider] Soft delete error on ${table}:`, error)
      return { success: false, error: error.message }
    }

    return { success: true }
  }

  async deleteMany(table: string, filters: QueryFilter[], deletionBatchId?: string): Promise<OperationResult<number>> {
    // Soft delete: set deleted_at timestamp and optional batch ID
    const updateData: { deleted_at: string; deletion_batch_id?: string } = { 
      deleted_at: new Date().toISOString() 
    }
    if (deletionBatchId) {
      updateData.deletion_batch_id = deletionBatchId
    }
    
    let query = supabase.from(table).update(updateData)

    for (const filter of filters) {
      query = this.applyFilter(query, filter)
    }

    const { error, count } = await query

    if (error) {
      logger.error(`[SupabaseProvider] Soft deleteMany error on ${table}:`, error)
      return { success: false, error: error.message }
    }

    return { success: true, data: count || 0 }
  }

  async hardDelete(table: string, id: string): Promise<OperationResult> {
    // Permanently delete: actually remove the row
    const { error } = await supabase
      .from(table)
      .delete()
      .eq('id', id)

    if (error) {
      logger.error(`[SupabaseProvider] Hard delete error on ${table}:`, error)
      return { success: false, error: error.message }
    }

    return { success: true }
  }

  async restore(table: string, id: string): Promise<OperationResult> {
    // Restore soft-deleted record: clear deleted_at and deletion_batch_id
    const { error } = await supabase
      .from(table)
      .update({ deleted_at: null, deletion_batch_id: null })
      .eq('id', id)

    if (error) {
      logger.error(`[SupabaseProvider] Restore error on ${table}:`, error)
      return { success: false, error: error.message }
    }

    return { success: true }
  }

  async restoreMany(table: string, filters: QueryFilter[]): Promise<OperationResult<number>> {
    // Restore multiple soft-deleted records: clear deleted_at and deletion_batch_id
    let query = supabase.from(table).update({ deleted_at: null, deletion_batch_id: null })

    for (const filter of filters) {
      query = this.applyFilter(query, filter)
    }

    const { error, count } = await query

    if (error) {
      logger.error(`[SupabaseProvider] RestoreMany error on ${table}:`, error)
      return { success: false, error: error.message }
    }

    return { success: true, data: count || 0 }
  }

  async restoreBatch(batchId: string): Promise<OperationResult<number>> {
    // Restore all records in a deletion batch across all tables
    const tables = Object.values(TABLES)
    let totalRestored = 0

    for (const table of tables) {
      try {
        const { count, error } = await supabase
          .from(table)
          .update({ deleted_at: null, deletion_batch_id: null })
          .eq('deletion_batch_id', batchId)

        if (!error && count) {
          totalRestored += count
        }
      } catch {
        // Table might not exist or have the column, skip
      }
    }

    return { success: true, data: totalRestored }
  }

  // ==================== Realtime / Cross-tab Sync ====================

  subscribe<T>(
    table: string,
    callback: (payload: ChangePayload<T>) => void,
    filters?: QueryFilter[]
  ): Subscription {
    const channelName = `${table}-changes-${Date.now()}`
    
    // Build filter string for postgres_changes
    let filterStr: string | undefined
    if (filters && filters.length > 0) {
      // Only supports simple eq filters for realtime
      const eqFilter = filters.find(f => f.operator === 'eq')
      if (eqFilter) {
        filterStr = `${eqFilter.column}=eq.${eqFilter.value}`
      }
    }

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: table,
          filter: filterStr
        },
        (payload) => {
          callback({
            eventType: payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE',
            table: table,
            old: payload.old as T | null,
            new: payload.new as T | null,
            errors: payload.errors
          })
        }
      )
      .subscribe()

    this.channels.set(channelName, channel)

    return {
      unsubscribe: async () => {
        await channel.unsubscribe()
        this.channels.delete(channelName)
      }
    }
  }

  subscribeToChannel(
    channelName: string,
    eventHandlers: Record<string, (payload: any) => void>
  ): Subscription {
    let channel = supabase.channel(channelName)

    for (const [event, handler] of Object.entries(eventHandlers)) {
      channel = channel.on('broadcast', { event }, (payload) => {
        handler(payload.payload)
      })
    }

    channel.subscribe()
    this.channels.set(channelName, channel)

    return {
      unsubscribe: async () => {
        await channel.unsubscribe()
        this.channels.delete(channelName)
      }
    }
  }

  async broadcast(channel: string, event: string, payload: any): Promise<void> {
    let existingChannel = this.channels.get(channel)
    
    if (!existingChannel) {
      existingChannel = supabase.channel(channel)
      await existingChannel.subscribe()
      this.channels.set(channel, existingChannel)
    }

    await existingChannel.send({
      type: 'broadcast',
      event,
      payload
    })
  }

  // ==================== File Storage ====================

  async uploadFile(bucket: string, path: string, file: Blob | File): Promise<OperationResult<string>> {
    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, file, { 
        upsert: true,
        // Cache for 1 year - files use unique UUIDs so they never change
        cacheControl: '31536000'
      })

    if (error) {
      logger.error(`[SupabaseProvider] Upload error:`, error)
      return { success: false, error: error.message }
    }

    return { success: true, data: path }
  }

  async getFileUrl(bucket: string, path: string, expiresIn: number = 3600): Promise<string | null> {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn)

    if (error) {
      logger.error(`[SupabaseProvider] GetFileUrl error:`, error)
      return null
    }

    return data.signedUrl
  }

  async deleteFile(bucket: string, path: string): Promise<OperationResult> {
    const { error } = await supabase.storage
      .from(bucket)
      .remove([path])

    if (error) {
      logger.error(`[SupabaseProvider] DeleteFile error:`, error)
      return { success: false, error: error.message }
    }

    return { success: true }
  }

  async listFiles(bucket: string, path: string): Promise<FileMetadata[]> {
    const { data, error } = await supabase.storage
      .from(bucket)
      .list(path)

    if (error) {
      logger.error(`[SupabaseProvider] ListFiles error:`, error)
      return []
    }

    return (data || []).map(file => ({
      path: `${path}/${file.name}`,
      size: file.metadata?.size || 0,
      contentType: file.metadata?.mimetype || 'application/octet-stream',
      createdAt: file.created_at,
      updatedAt: file.updated_at || file.created_at
    }))
  }

  // ==================== Auth ====================

  getAuthState(): AuthState {
    const session = supabase.auth.getSession()
    // This is sync, we need to use cached state
    // The actual state is managed by the auth store
    return {
      user: null, // Will be populated by auth store
      isAuthenticated: false
    }
  }

  getCurrentUserId(): string | null {
    // Access synchronously from cached session
    // Note: This should be used carefully - prefer using auth store
    // Supabase stores session with key pattern: sb-<project-ref>-auth-token
    // Find any matching key since project ref can vary (local dev vs production)
    const authKeys = Object.keys(localStorage).filter(k => k.startsWith('sb-') && k.endsWith('-auth-token'))
    
    for (const storageKey of authKeys) {
      const storedSession = localStorage.getItem(storageKey)
      if (storedSession) {
        try {
          const parsed = JSON.parse(storedSession)
          const userId = parsed?.user?.id
          if (userId) {
            return userId
          }
        } catch {
          // Continue to next key
        }
      }
    }
    return null
  }

  onAuthStateChange(callback: (state: AuthState) => void): Subscription {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      callback({
        user: session?.user ? {
          id: session.user.id,
          email: session.user.email || null,
          username: session.user.user_metadata?.username || null,
          createdAt: session.user.created_at
        } : null,
        isAuthenticated: !!session
      })
    })

    return {
      unsubscribe: () => subscription.unsubscribe()
    }
  }

  // ==================== Provider Info ====================

  getMode(): 'online' | 'standalone' {
    return 'online'
  }

  isReady(): boolean {
    return this.initialized
  }

  async initialize(): Promise<void> {
    logger.info('[SupabaseProvider] Initializing...')
    
    // Verify connection by attempting to get session
    await supabase.auth.getSession()
    
    this.initialized = true
    logger.info('[SupabaseProvider] Initialized successfully')
  }

  async cleanup(): Promise<void> {
    logger.info('[SupabaseProvider] Cleaning up...')
    
    // Unsubscribe from all channels
    for (const [name, channel] of this.channels) {
      await channel.unsubscribe()
      logger.debug(`[SupabaseProvider] Unsubscribed from channel: ${name}`)
    }
    this.channels.clear()
    
    this.initialized = false
    logger.info('[SupabaseProvider] Cleanup complete')
  }

  // ==================== Private Helpers ====================

  private applyFilter(query: any, filter: QueryFilter): any {
    switch (filter.operator) {
      case 'eq':
        return query.eq(filter.column, filter.value)
      case 'neq':
        return query.neq(filter.column, filter.value)
      case 'gt':
        return query.gt(filter.column, filter.value)
      case 'gte':
        return query.gte(filter.column, filter.value)
      case 'lt':
        return query.lt(filter.column, filter.value)
      case 'lte':
        return query.lte(filter.column, filter.value)
      case 'like':
        return query.like(filter.column, filter.value)
      case 'ilike':
        return query.ilike(filter.column, filter.value)
      case 'in':
        return query.in(filter.column, filter.value)
      case 'is':
        return query.is(filter.column, filter.value)
      case 'contains':
        return query.contains(filter.column, filter.value)
      default:
        logger.warn(`[SupabaseProvider] Unknown filter operator: ${filter.operator}`)
        return query
    }
  }
}
